import { useMemo, useState } from 'react';
import { validatePhoneNumber } from 'common/utils/validators';
import {
  CHANNEL_ITEMS,
  DOCUMENT_DELIVERY_ITEMS,
  type CommunicationPreferencesValues,
  type ContactField,
  type ContactInformation,
  type ToggleGroupKey,
  type ToggleGroupState,
  type ToggleItemConfig,
} from './communication-preferences.types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_CONTACT: ContactInformation = {
  mobilePhone: '',
  preferredPhone: '',
  email: '',
};

const buildGroup = (items: ToggleItemConfig[]): ToggleGroupState => ({
  master: false,
  items: items.reduce<Record<string, boolean>>((accumulator, { id }) => {
    accumulator[id] = false;
    return accumulator;
  }, {}),
});

// Everything off by default. The back end seeds real values via `initialValues`
// in GEIS-359.
const buildDefaultGroups = (): Record<ToggleGroupKey, ToggleGroupState> => ({
  documentDelivery: buildGroup(DOCUMENT_DELIVERY_ITEMS),
  careWellness: buildGroup(CHANNEL_ITEMS),
  promotions: buildGroup(CHANNEL_ITEMS),
});

// Set the master and every child of a group to the same value.
const setGroupAll = (
  group: ToggleGroupState,
  value: boolean,
): ToggleGroupState => ({
  master: value,
  items: Object.keys(group.items).reduce<Record<string, boolean>>(
    (accumulator, id) => {
      accumulator[id] = value;
      return accumulator;
    },
    {},
  ),
});

const isPhoneValid = (value: string) =>
  value.trim() === '' || validatePhoneNumber(value);

const isEmailValid = (value: string) =>
  value.trim() === '' || EMAIL_PATTERN.test(value.trim());

type ContactErrorKey =
  | 'STR_COMM_PREF_PHONE_ERROR'
  | 'STR_COMM_PREF_EMAIL_ERROR';

export type ContactErrors = Partial<Record<ContactField, ContactErrorKey>>;

export type UseCommunicationPreferencesFormParams = {
  // Seed the form from the back end (GEIS-359). Defaults to everything
  // off/empty. The owning page should gate rendering on its query so the form
  // mounts once with real values — keeping state derived from props, no effect.
  initialValues?: CommunicationPreferencesValues;
  // Persist handler, wired to the comms API in GEIS-359. Receives the current
  // assembled values plus the values the form was seeded with at mount, so the
  // owning page can diff the PATCH delta against the original seed (not the
  // latest query data, which a background refetch can change mid-edit).
  onSave?: (
    values: CommunicationPreferencesValues,
    seededValues: CommunicationPreferencesValues,
  ) => void;
};

/**
 * State + rules for the Communication Preferences form (GEIS-358).
 *
 * - A group's master toggle turns all of its child toggles on/off together.
 * - Turning any child off flips the master off; turning the last child on
 *   flips the master on (per PRD S-5).
 * - Contact fields validate before Save is enabled. The back end is wired in
 *   GEIS-359 via `initialValues` (seed) and `onSave` (persist); the server
 *   owns the Care, health and wellness opt-in, so there is no client-side
 *   auto-opt-in here.
 */
export const useCommunicationPreferencesForm = ({
  initialValues,
  onSave,
}: UseCommunicationPreferencesFormParams = {}) => {
  const [contact, setContact] = useState<ContactInformation>(
    () => initialValues?.contact ?? EMPTY_CONTACT,
  );

  const [groups, setGroups] = useState<
    Record<ToggleGroupKey, ToggleGroupState>
  >(() => initialValues?.groups ?? buildDefaultGroups());

  const toggleMaster = (key: ToggleGroupKey) => {
    setGroups((previous) => ({
      ...previous,
      [key]: setGroupAll(previous[key], !previous[key].master),
    }));
  };

  const toggleItem = (key: ToggleGroupKey, itemId: string) => {
    setGroups((previous) => {
      const group = previous[key];
      const items = { ...group.items, [itemId]: !group.items[itemId] };
      // Keep `{ master, items }` key order consistent with buildGroup/setGroupAll
      // so the serialized dirty check (below) sees a true revert as not dirty.
      return {
        ...previous,
        [key]: { master: Object.values(items).every(Boolean), items },
      };
    });
  };

  const setContactField = (field: ContactField, value: string) => {
    setContact((previous) => ({ ...previous, [field]: value }));
  };

  const errors = useMemo<ContactErrors>(
    () => ({
      mobilePhone: isPhoneValid(contact.mobilePhone)
        ? undefined
        : 'STR_COMM_PREF_PHONE_ERROR',
      preferredPhone: isPhoneValid(contact.preferredPhone)
        ? undefined
        : 'STR_COMM_PREF_PHONE_ERROR',
      email: isEmailValid(contact.email)
        ? undefined
        : 'STR_COMM_PREF_EMAIL_ERROR',
    }),
    [contact],
  );

  const isValid = useMemo(
    () => Object.values(errors).every((error) => error === undefined),
    [errors],
  );

  const values = useMemo<CommunicationPreferencesValues>(
    () => ({ contact, groups }),
    [contact, groups],
  );

  // The values the form was seeded with, captured once at mount via a lazy
  // initializer — not a render-time ref or an effect. isDirty compares the live
  // values against this (to gate Save and drive the LeaveWithoutSavingModal),
  // and handleSave hands it to the page so PATCH deltas diff against the seed,
  // not the latest query data (a background refetch can change that mid-edit).
  // The page re-mounts the form on save success, so the next mount re-captures
  // the seed from the refetched server values; a failed save leaves the form
  // dirty so Save stays available. Stable key order across updates makes a
  // structural revert read as not dirty.
  const [seededValues] = useState<CommunicationPreferencesValues>(() => ({
    contact: initialValues?.contact ?? EMPTY_CONTACT,
    groups: initialValues?.groups ?? buildDefaultGroups(),
  }));
  const [savedSnapshot] = useState(() => JSON.stringify(seededValues));

  const isDirty = useMemo(
    () => JSON.stringify(values) !== savedSnapshot,
    [values, savedSnapshot],
  );

  // The save mutation is wired by the page (GEIS-359). Hands the page both the
  // current values and the mount seed so it can build a PATCH delta; the dirty
  // baseline is reset by the page re-mounting the form on save success — not
  // here — so a failed save keeps the form dirty and Save available.
  const handleSave = () => {
    onSave?.(values, seededValues);
  };

  return {
    contact,
    setContactField,
    groups,
    toggleMaster,
    toggleItem,
    errors,
    isValid,
    isDirty,
    values,
    handleSave,
  };
};
